import { Link, Navigate, useParams } from "react-router-dom";
import { findInsight, formatInsightDate, INSIGHTS } from "@/lib/insights";
import { SafeImage } from "@/components/site/SafeImage";
import { useSeo } from "@/hooks/useSeo";
import { useJsonLd } from "@/hooks/useJsonLd";
import { absoluteUrl, blogPostingLd, breadcrumbLd } from "@/lib/structured-data";
import { useSiteConfig, whatsappHref } from "@/lib/site-config";

export function ArticlePage() {
  const { slug } = useParams();
  const article = findInsight(slug);
  const siteConfig = useSiteConfig();

  useSeo({
    title: article ? article.seoTitle : "Insights | Hive Homes",
    description: article
      ? article.seoDescription
      : "Hive Homes Dubai insights.",
    // Use the article's own cover for social previews, falling back to a
    // brand-consistent Dubai image if one isn't set.
    ogImage: absoluteUrl(article?.image ?? "/images/site/insights.jpg"),
    ogType: "article",
  });

  useJsonLd("article", article ? blogPostingLd(article) : null);
  useJsonLd(
    "article-breadcrumb",
    article
      ? breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: article.title, path: `/insights/${article.slug}` },
        ])
      : null,
  );

  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  const related = INSIGHTS.filter((a) => a.slug !== article.slug).slice(0, 2);
  const waLink = siteConfig.whatsapp.set
    ? `${whatsappHref(siteConfig.whatsapp)}?text=${encodeURIComponent(
        `Hi Hive Homes, I just read your article "${article.title}" and would like to chat.`,
      )}`
    : null;

  return (
    <>
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide py-12 md:py-16 short:py-8">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em] text-[var(--text-muted)] hover:text-[var(--gold-bright)]"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M12 7H2m4-4-4 4 4 4" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            All Insights
          </Link>

          <p className="mt-6 text-[11.5px] uppercase tracking-[0.18em] text-[var(--gold-bright)]">
            {article.category}
          </p>
          <h1
            className="mt-4 max-w-[24ch] font-display text-[32px] leading-[1.05] text-[var(--text)] sm:mt-5 sm:text-[44px] md:text-[64px] md:leading-[1.02]"
            style={{ fontVariationSettings: '"opsz" 96' }}
          >
            {article.title}
          </h1>
          <p className="mt-5 text-[12px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
            {formatInsightDate(article.date)} · {article.readMinutes} min read
          </p>
        </div>
      </section>

      <div className="container-wide pt-10 md:pt-14">
        <div className="relative aspect-[16/9] overflow-hidden bg-[var(--surface-2)] md:aspect-[5/2]">
          <SafeImage
            src={article.image}
            alt={article.title}
            fetchPriority="high"
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      <article className="container-wide grid gap-10 py-12 md:grid-cols-12 md:gap-14 md:py-16 short:py-8">
        <div className="md:col-span-8">
          <p className="text-[17.5px] leading-[1.7] text-[var(--text)] sm:text-[19px] sm:leading-[1.75]">
            {article.excerpt}
          </p>

          <div className="mt-10 flex flex-col gap-8">
            {article.sections.map((section, i) => (
              <div key={i}>
                {section.heading && (
                  <h2
                    className="font-display text-[22px] leading-tight text-[var(--text)] sm:text-[26px]"
                    style={{ fontVariationSettings: '"opsz" 36' }}
                  >
                    {section.heading}
                  </h2>
                )}
                <div className="mt-4 flex flex-col gap-4 text-[15.5px] leading-[1.8] text-[var(--text-soft)] sm:text-[16.5px]">
                  {section.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col gap-3 border-y border-[var(--line)] py-10 sm:flex-row sm:items-center sm:justify-between">
            <p
              className="font-display text-[22px] text-[var(--text)] sm:text-[24px]"
              style={{ fontVariationSettings: '"opsz" 36' }}
            >
              {article.ctaLabel}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn btn-primary">
                {article.ctaLabel}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2 7h10m-4-4 4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                </svg>
              </Link>
              {waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                >
                  WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>

        <aside className="md:col-span-4 md:sticky md:top-32 md:self-start">
          <div className="border border-[var(--line-strong)] bg-[var(--surface)] p-6 sm:p-7">
            <p className="eyebrow">Related Reading</p>
            <ul className="mt-5 flex flex-col gap-5">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/insights/${r.slug}`}
                    className="group block"
                  >
                    <p className="text-[10.5px] uppercase tracking-[0.18em] text-[var(--gold-bright)]">
                      {r.category}
                    </p>
                    <p
                      className="mt-1.5 font-display text-[17px] leading-tight text-[var(--text)] transition-colors group-hover:text-[var(--gold-bright)]"
                      style={{ fontVariationSettings: '"opsz" 36' }}
                    >
                      {r.title}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </article>
    </>
  );
}
